import React, { useContext, useEffect, useState } from 'react';
import DefaultCard from '../../components/default/DefaultCard';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import DefaultModal from '../../components/default/DefaultModal';
import useHolidayService from '../../app/services/hooks/useHolidayService';
import { Holiday, HolidayFilters } from '../../app/models/Holiday';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';
import HolidayDataTable from './components/tables/HolidayDataTable';
import HolidayCreate from './components/HolidayCreate';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import HolidayEdit from './components/HolidayEdit';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Feriados',
        url: '/holidays',
        isActive: true
    }
];

const Holidays = () => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetHolidays, getHolidays, deleteHoliday } = useHolidayService();
    const { navigationPage } = useNavigationPage();

    const [holidayIdEdit, setHolidayIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [holidayFilter, setHolidayFilter] = useState<HolidayFilters>({
        year: new Date().getFullYear().toString(),
        page: 1,
        per_page: 10,
        sort: 'date',
        order: 'asc'
    });

    useEffect(() => {
        reloadTable();
    }, [holidayFilter.page, holidayFilter.per_page, holidayFilter.sort, holidayFilter.order]);

    const getAllHolidays = () => {
        getHolidays(holidayFilter, {
            onSuccess: (response: ServiceResponse) => {
                setHolidays(response.data.holidays);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const destroy = (holidayId: number) => {
        const _text = 'Está a punto de eliminar el feriado #' + holidayId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando feriado...');
                        deleteHoliday(holidayId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El feriado no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const reloadTable = () => {
        getAllHolidays();
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (holidayId: number) => {
        setShowingEdit(true);
        setHolidayIdEdit(holidayId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setHolidayIdEdit(-1);
    };

    return (
        <>
            <Breadcrumbs pageSection="Feriados" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo feriado" />}
                />
                <HolidayDataTable
                    holidays={holidays}
                    totalRows={totalRows}
                    filter={holidayFilter}
                    setFilter={setHolidayFilter}
                    callbackSuccess={reloadTable}
                    destroy={destroy}
                    edit={showEdit}
                    loading={fetchingGetHolidays}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Feriado"
                    backdrop={true}
                    showFooter={false}
                >
                    <HolidayCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Feriado"
                    backdrop={true}
                    showFooter={false}
                >
                    <HolidayEdit
                        holidayId={holidayIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Holidays;
